<script setup lang="ts">
import { useHead } from '@unhead/vue';
import citiesJson from '../../../json/cities-with-events.json';
import eventsJson from '../../../json/events.json';
import { UkTouhouEvent, UkTouhouEventRaw } from '../../classes/UkTouhouEvent';
import UktEventCard from '../UktEventCard.vue';
import bannerUrl from '../../img/stone-henge.png';

interface Coordinates {
  latitude: number;
  longitude: number;
}
interface City {
  name: string;
  coordinates: Coordinates;
}
interface Country {
  name: string;
  cities: City[];
}

const cityCountries: Country[] = (citiesJson as Country[]).map(country => ({
  ...country,
  cities: [...country.cities].sort((a, b) => a.name.localeCompare(b.name))
}));

const events: UkTouhouEvent[] = (eventsJson as UkTouhouEventRaw[])
  .map(eventRaw => new UkTouhouEvent(eventRaw))
  .filter(event => event.isUpcoming || event.isInProgress)
  .sort((a: UkTouhouEvent, b: UkTouhouEvent) => a.begin - b.begin);

const nextEvent: UkTouhouEvent | null = events.length > 0 ? events[0] : null;
const nextEventText: string = nextEvent
  ? `Our next event is ${nextEvent.name} 🗓️ ${nextEvent.dateToString} 📍 ${nextEvent.locationOneLine}`
  : '';

useHead({
  title: 'UK Touhou Community - Events',
  meta: [
    { name: 'description', content: `Upcoming Touhou events in the UK! ${nextEventText}` }
  ]
});


</script>

<template>
  <main class="fullscreen-page" :style="`background-image: url(${bannerUrl})`">
    <div class="page-overlay">
      <div class="content-container">
        <section class="padded-content">
          <div class="page-header-row">
            <h1 class="page-heading">Events</h1>
            <router-link to="/" class="back-link">Back to homepage</router-link>
          </div>
          <h2>About</h2>

          <p>
            We meet in person quite often! We hold meets in many parts of the UK. These usually take place in a cafe,
            pub, as a picnic in a park or even a stroll around the city. We also try to hold a meetup at every major
            convention.
          </p>

          <p>
            Previous meets have taken place in the following locations:
          </p>

          <ul>
            <template v-for="country in cityCountries" :key="country.name">
              <li v-if="country.cities.length > 0">
                {{ country.name }}: {{country.cities.map(city => city.name).join(', ')}}
              </li>
            </template>
          </ul>

          <p>Whether your city is listed or not, we're always looking for volunteers to help host new events, that could
            be you!
          </p>

          <p>
            We discuss and post updates on the events inside the Discord server, so we highly recommend joining it if
            you're
            interested in attending any of the events!
          </p>

          <div class="discord-section">
            <a class="hero-button discord-button" v-discord-invite>
              <img src="../../img/Discord-Symbol-White.svg" alt="Discord Logo">
              <span>Join Discord</span>
            </a>
          </div>
        </section>

        <section id="scheduled-events" class="padded-content" v-if="events.length > 0">
          <h2>Scheduled events</h2>

          <div class="calendar-info">
            <p>The events listed below are also available as a subscribable .ics calendar:</p>
            <div class="box">https://uktouhou.org/uk-touhou-events.ics</div>
            <p class="small">
              example instructions: <a target="_blank"
                href="https://support.google.com/calendar/answer/37100">Google</a>,
              <a target="_blank" href="https://support.apple.com/en-gb/guide/calendar/icl1022/mac">Apple</a>,
              <a target="_blank"
                href="https://support.microsoft.com/en-gb/office/import-or-subscribe-to-a-calendar-in-outlook-com-or-outlook-on-the-web-cff1429c-5af6-41ec-a5b4-74f2c278e98c">Outlook</a>
            </p>
          </div>

          <ukt-event-card v-for="event in events" :key="event.id" :event="event"></ukt-event-card>
        </section>
        <section class="padded-content" v-else>
          <h2>Scheduled events</h2>
          <div class="no-events">There are no events scheduled at the moment.<br>Join the <a v-discord-invite>Discord
              server</a>
            to suggest some!</div>
        </section>
      </div>
    </div>
  </main>
</template>

<style scoped>
.page-overlay {
  background: hsl(0 0% 0% / 15%);
}

.page-header-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--space-md);
}

.back-link {
  font-family: var(--font-display);
  font-size: var(--font-size-lg);
  color: var(--color-link);
  text-decoration: none;
  font-weight: 400;

  &:hover {
    color: var(--color-link-hover);
    text-decoration: underline;
  }
}

.content-container {
  flex: 1;
  max-width: 750px;
  width: 100%;
  margin: 0 auto;
  background-color: hsl(0 0% 0% / 85%);
}

.content-container h2 {
  font-size: var(--font-size-2xl);
  font-weight: 400;
}


.calendar-info {
  margin-bottom: var(--space-xl);
  padding: var(--space-md);
  background: var(--color-bg-overlay-white-light);
  border: 1px solid var(--color-border-subtle);
  border-radius: 4px;
}

.discord-section {
  display: flex;
  justify-content: center;
  margin-top: var(--space-lg);
}

.discord-button {
  gap: var(--space-xs);
  padding: var(--space-md) var(--space-xl);
  font-size: var(--font-size-lg);

  img {
    height: 1.25rem;
  }
}

.no-events {
  font-style: italic;
  text-align: center;
  color: var(--color-text-white-80);
}

.box {
  border: 1px solid;
  width: fit-content;
  padding: var(--space-xs) var(--space-md);
  margin-bottom: var(--space-xs);
  user-select: all;
  font-family: var(--font-mono);
}
</style>
